import { ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useRef, useState } from 'react'
import { Button } from '../utils/Button'
import intl from 'react-intl-universal'
import MagicWandSparklesIcon from '../icons/MagicWandSparkles'
import { track, trackAiCreation, trackButtonEnhanced } from 'utils/track'
import { useAppState } from 'graphql/hooks/useAppState'
import { capitalize, getMeta } from 'utils/functions'
import { useMutation, useQuery } from '@apollo/client'
import { getCurrentSubscriptionQuery, GetCurrentSubscriptionRes, GetCurrentSubscriptionVars } from 'graphql/queries/user/getUser'
import { GetOrganizationsRes, GetOrganizationsVars, getOrganizationsQuery } from 'graphql/queries/organization/getOrganziations'
import { useActiveWorkspaceId } from 'hooks/useActiveWorkspaceId'
import Lottie from 'lottie-react'
import outlineSkeleton from 'common/json/lottie/OutlineSkeleton.json'
import { updateAppCache } from 'app'
import { sendBillingAdminLockedFeatureEmailMutation, SendBillingAdminLockedFeatureEmailVars } from 'graphql/mutations/emails/sendUserLockedFeatureEmail'
import MagicCredits from './components/MagicCredits'
import AdditionalInfo from './components/AdditionalInfo'
import CreationInProgress from './components/CreationInProgress'
import MagicUploader from './components/MagicUploader'
import OutlineContent from './components/OutlineContent'
import StepTitle from './components/StepTitle'
import MagicTextArea from './components/TextArea'
import { contentTypes, useCases, programLength, sessionLength, audienceOptions, voice, programDifficulty, languages } from './constants'
import { createSessionOrAction, createProgram, createProgramOutline, generateCustomizeSuggestions } from './mutations'
import { wrapperInputCn, labelCn, inputAreaCn, recreateOutlineBtns } from './styles'
import { MagicCreatorProps, CustomFile, MagicCreditsResponse, FileData, MagicType } from './types'
import { checkAndShowAiCreditsModal } from './utilis'
import MagicSelect from './components/MagicSelect'
import MagicCheckbox from './components/Checkbox'
import { ProgramSummaryField, ProgramFinalCosts } from './components/ProgramSummary'
import { showModal2 } from 'components/utils/CustomModal'
import { extractGeminiErrorMessage } from 'utils/utils'
import AliceCarousel from 'react-alice-carousel'
import ExitMagicCreatorAlert from './components/ExitMagicCreatorAlert'
import { useDrawer } from 'context/drawer'
import Tippy from '@tippyjs/react'
import { NewTag } from 'components/navbar/Navbar'

const OutlineAlert = ({onContinue, closeModal}) => {
  return (
      <div className="text-center">
          <img src="/images/magic-alert.svg" className="w-20 h-20 mx-auto mb-10" />
          <div className="font-bold text-lg mb-2">Are you sure you want to go back</div>
          <div>If you leave this screen, all created outlines will be lost.</div>
          <div className="flex gap-x-3 mx-auto mt-10 px-20">
              <Button className="w-full" text="Lose Outline(s)" onClick={onContinue} />
              <Button className="w-full" text="Stay" type="magic" onClick={closeModal} />
          </div>
      </div>
    );
}

const ProgressIndicator = ({ currentStep }: { currentStep: number }) => {
  const steps = [
    { number: 1, label: intl.get('magic_progress_details') },
    { number: 2, label: intl.get('magic_progress_customize') },
    { number: 3, label: intl.get('magic_progress_outline') },
    { number: 4, label: intl.get('magic_progress_summary') },
  ]

  return (
    <div className="flex items-center justify-center mb-6 px-8 sm:px-0">
      {steps.map((step, index) => (
        <div key={step.number} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                currentStep >= step.number
                  ? 'bg-magic text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}
            >
              {step.number}
            </div>
            <div className={`text-xs mt-1 ${currentStep >= step.number ? 'text-magic font-semibold' : 'text-gray-500'}`}>
              {step.label}
            </div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-12 sm:w-20 h-0.5 mb-5 ${
                currentStep > step.number ? 'bg-magic' : 'bg-gray-300'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  )
}


const MagicCreator = ({ typeOfCreator, courseId, chapterId, closeModal, onPrevious, whiteLabelData, showZendeskWidget }: MagicCreatorProps) => {
  const typeOfCreatorProgram = typeOfCreator === 'course'
  const typeOfCreatorSession = typeOfCreator === 'chapter'
  const typeOfCreatorAction = typeOfCreator === 'action'
  const appState = useAppState()
  const [creationLoader, setCreationLoader] = useState(false)
  const [showDetails, setShowDetails] = useState(typeOfCreatorProgram ? true : false)
  const [showOptions, setShowOptions] = useState(false)
  const [showEditOutline, setShowEditOutline] = useState(false)
  const [prompt, setPrompt] = useState<string>('')
  const [selectedFiles, setSelectedFiles] = useState<CustomFile[]>([])
  const [showUploadIndicator, setShowUploadIndicator] = useState(false)
  const [showCreditsIndicator, setShowCreditsIndicator] = useState(true)
  const [disableCreateButton, setDisableCreateButton] = useState(false)
  const [programStep, setProgramStep] = useState(1)
  const [expanded, setExpanded] = useState(false)
  const [describeChanges, setDescribeChanges] = useState('')
  const [outlineLoading, setOutlineLoading] = useState(false)
  const [outlineData, setOutlineData] = useState('')
  const [otherAudienceValue, setOtherAudienceValue] = useState('')
  const [customizeSuggestionsLoading, setCustomizeSuggestionsLoading] = useState(false)
  const [programLengthOther, setProgramLengthOther] = useState<string | number>('')
  const [userModifiedFields, setUserModifiedFields] = useState<Set<string>>(new Set())
  const [suggestionsFetched, setSuggestionsFetched] = useState(false)
  const [outlineDataArray, setOutlineDataArray] = useState<{ title: string; description: string; sessions: { sessionNumber: string; title: string; description: string }[] }[]>([]);
  const [outlinePosition, setOutlinePosition] = useState(0)
  const [outlineDataString, setOutlineDataString] = useState<string[]>([])
  const [outlineCreditsArray, setOutlineCreditsArray] = useState<MagicCreditsResponse['credits'][]>([])
  const [credits, setCredits] = useState<MagicCreditsResponse['credits']>({
    totalTokens: 0,
    totalCredits: 0,
    availableCredits: 0,
  })
  const [magicData, setMagicData] = useState({
    title: '',
    type: typeOfCreatorAction ? 'automatic' : undefined,
    description: '',
    courseDescription: '',
    useCase: 'automatic',
    language: 'automatic',
    programLength: typeOfCreator !== 'action' ? 'automatic' : undefined,
    audience: 'automatic',
    audienceOther: '',
    voice: 'automatic',
    fileMatchingType: '',
    fileUrls: [],
    uploadedFilesData: [] as FileData[],
    generateAiImages: true,
    generateAiAudio: typeOfCreator !== 'action' ? false : false,
    generateAiVideo: typeOfCreator !== 'action' ? false : false,
    // generateAiAudio: false,
    learnerOutcomes: '',
    programDifficulty: 'automatic',
    programSuccess: '',
    useEmoji: true,
  } as MagicType)
  const { setCurrentStep } = useDrawer()

  const parseOutlineData = (data) => {
    if (!data) return { title: '', description: '', sessions: [] }
    const lines = data.split('\n') // Split the string into lines
    const titleLine = lines.find((line) => line.startsWith('Title of Program:')) // Extract the title
    const title = titleLine.replace('Title of Program: ', '').trim() // Remove prefix and trim whitespace
    const descriptionLine = lines.find((line) => line.startsWith('Description of Program:')) // Extract the description
    const description = descriptionLine.replace('Description of Program: ', '').trim() // Remove prefix and trim whitespace

    // Extract session data
    const sessions: { sessionNumber: string; title: string; description: string }[] = []
    let currentSession = { sessionNumber: '', title:'', description: '', }

    lines.forEach((line) => {
      line = line.trim() // Remove extra spaces
      if (line.startsWith('Session')) {
        if (currentSession) {
          sessions.push(currentSession); // Add the previous session to the list
        }
        currentSession = { sessionNumber: line, title: '', description: '' }
      } else if (line.startsWith('Title:')) {
        currentSession.title = line.replace('Title: ', '')
      } else if (line.startsWith('Description:')) {
        currentSession.description = line.replace('Description: ', '');
      }
    })

    // Push the last session
    if (currentSession) {
      sessions.push(currentSession)
    }

    return { title, description, sessions }
  }

  useEffect(() => {
    if (outlineData) {
      const parsedData = parseOutlineData(outlineData) // Process outlineData
        setOutlineDataString(prevArray => [...prevArray, outlineData]) // Append new data
        setOutlineDataArray(prevArray => [...prevArray, parsedData]) // Store parsed data
    }
  }, [outlineData])

  useEffect(() => {
    if (outlineData) {
      setOutlineCreditsArray(prevArray => {
        if (prevArray.length === outlineDataArray.length) {
          // Replace the last entry if the lengths match
          return [...prevArray.slice(0, -1), credits];
        } else {
          // Otherwise, append (only when outlineData changes)
          return [...prevArray, credits];
        }
      });
    }
  }, [outlineData, credits])

  useEffect(() => {
    if (outlineDataArray.length > 1 ) {
      const newPosition = outlineDataArray.length - 1;
      setOutlinePosition(newPosition);
    }
  }, [outlineDataArray])



  useEffect(() => {
    if (window.zE) {
      window.zE('webWidget', 'hide')
    }
  }, [])

  useEffect(() => {
    const hasMagicData =
      magicData.title ||
      magicData.learnerOutcomes ||
      magicData.programSuccess ||
      selectedFiles.length > 0 ||
      outlineDataString.length > 0
  
    setCurrentStep(hasMagicData ? 'magic' : '')
  }, [magicData.title, magicData.learnerOutcomes, magicData.programSuccess, selectedFiles, outlineDataString])

  const isAudienceOther = magicData.audience === 'other'

  const { data: subData, loading: subLoading } = useQuery<GetCurrentSubscriptionRes, GetCurrentSubscriptionVars>(getCurrentSubscriptionQuery, {
    skip: !appState,
    variables: { userId: appState!.loggedInAs.uid },
  })

  // Get organization data for feature flags
  const activeWorkspaceId = useActiveWorkspaceId()
  const { data: orgData } = useQuery<GetOrganizationsRes, GetOrganizationsVars>(getOrganizationsQuery, {
    skip: !appState,
    variables: {
      searchText: '',
      skip: 0,
      limit: 100,
      limitToUserOrganizations: appState?.loggedInAs.uid || '',
      limitToManagerOrganizations: appState?.loggedInAs.uid || '',
      disabled: false,
      includeAgentEditorCuratorGroupOrgs: true,
    },
    errorPolicy: 'all',
  })

  const [sendBillingAdminLockedFeatureEmail] = useMutation<{ sendUserLockedFeatureEmail: string }, SendBillingAdminLockedFeatureEmailVars>(sendBillingAdminLockedFeatureEmailMutation)

  useEffect(() => {
    setDisableCreateButton(showUploadIndicator || showCreditsIndicator)
  }, [showUploadIndicator, showCreditsIndicator])

  const organizationId: string = getMeta(subData?.user.metadata, 'activeWorkspaceId')

  // Billing-admin-ness on its own, kept because the legacy workspace-pool upsell
  // still keys off it. It does **not** exempt anybody from per-creator credits —
  // an admin who is not on the AI Tasks list has zero AI Tasks by design.
  const isBillingAdmin = subData?.user.billingManagerOrganizations.includes(organizationId) || false

  // Wider than the above, and a different question: who may assign AI Tasks at
  // all. The roster is managed by anyone api-2's `assertCanManage` accepts, so a
  // workspace admin who is not the billing contact can self-assign — "ask your
  // admin" would point them at themselves. `isBillingAdmin` stays billing-only
  // because it drives the purchase/upgrade paths.
  const canAssignAiTasks = isBillingAdmin
    || (subData?.user.managerOrganizations?.includes(organizationId) || false)

  /**
   * Whether AI Tasks have already been requested in this session, by the inline
   * link *or* the modal button.
   *
   * Held here because those two used to keep separate state, so asking through one
   * left the other live and a second request went out on the next click.
   */
  const [aiCreditsRequested, setAiCreditsRequested] = useState(false)
  
  // Filter contentTypes based on feature flags
  const getFilteredContentTypes = () => {
    const hasAudioFeature = orgData?.res.organizations.find(o => o.id === activeWorkspaceId)?.featureFlags.includes('generateAudioAi')

    // AI video is made only by the new app (GNB-3141): the API ignores it here (/api/v1), so a "video" action would be
    // created empty. Hidden for every workspace, flag or not.
    const filteredTypes = Object.entries(contentTypes).filter(([key]) => {
      if (key === 'audio' && !hasAudioFeature) return false
      if (key === 'video') return false
      return true
    })
    
    return filteredTypes
  }

  const filteredContentTypes = getFilteredContentTypes()

  // Helper to check if any customize fields are set to 'automatic'
  const hasAutomaticFields = () => {
    const customizeFields: (keyof typeof magicData)[] = ['useCase', 'language', 'programLength', 'audience', 'voice', 'programDifficulty']
    return customizeFields.some(field => magicData[field] === 'automatic')
  }

  if (!appState) return null

  const [companyId] = appState.currentCompanyIdCourseId.split('-') || ['', '']

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    // Track if this is a customize field being modified on step 2
    const customizeFields = ['useCase', 'language', 'programLength', 'audience', 'voice', 'programDifficulty']
    if (typeOfCreatorProgram && programStep === 2 && customizeFields.includes(name)) {
      if (value !== 'automatic') {
        // User manually selected a non-automatic value - mark as modified
        setUserModifiedFields(prev => new Set(prev).add(name))
      } else {
        // User manually set back to automatic - remove from modified fields
        setUserModifiedFields(prev => {
          const newSet = new Set(prev)
          newSet.delete(name)
          return newSet
        })
      }
    }

    setMagicData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]: value,
      }

      // Auto-enable audio/video generation when type is audio/video for actions
      // used for Magic Action Creator only, to estimate credits correctly
      if (name === 'type' && typeOfCreatorAction) {
        if (value === 'audio') {
          updatedData.generateAiAudio = true
          updatedData.generateAiVideo = false
        } else if (value === 'video') {
          updatedData.generateAiVideo = true
          updatedData.generateAiAudio = false
        }
      }

      return updatedData
    })
  }

  const onChange = (e) => {
    setOtherAudienceValue(e.target.value); // Update the "Other" input value
  };

  const handleProgramLengthOtherChange = (e) => {
    // Track that programLength was modified on step 2
    if (typeOfCreatorProgram && programStep === 2 && e.target.value) {
      setUserModifiedFields(prev => new Set(prev).add('programLength'))
    }
    setProgramLengthOther(e.target.value)
  }

  const handleMagicActionCreate = async () => {
    // check if the user has enough credits
    const upsellModalShown = checkAndShowAiCreditsModal(credits, subData, organizationId, sendBillingAdminLockedFeatureEmail, () => setAiCreditsRequested(true))
    if (upsellModalShown) return

    const isE2eTest = localStorage.getItem("e2eTest") === "true"
    if (!courseId) return
    setCreationLoader(true)

    const title = typeOfCreatorAction ? intl.get('magic_action_creation_in_progress_title') : intl.get('magic_session_creation_in_progress_title')
    const description = typeOfCreatorAction ? intl.get('magic_action_creation_in_progress_description') : intl.get('magic_session_creation_in_progress_description')

    const modalCreationInProcess = showModal2({
      component: <CreationInProgress title={title} description={description}/>,
      disableClickOutside: true,
      onlyContent: true,
      secondaryButton: false,
    })

    const enrichedFilesData = magicData.uploadedFilesData.map(file => {
      const matchingSelectedFile = selectedFiles.find(
        selected => selected.geminiName === file.name
      )

      return {
        ...file,
        filePrecision: matchingSelectedFile?.filePrecision || 'high_precision',
        uploadMethod: matchingSelectedFile?.uploadMethod || 'use_inspiration',
      }
    })

    const mappingFilesWithPrecision = selectedFiles.map((file) => ({
      filePrecision: file.filePrecision ? file.filePrecision : 'high_precision',
      uploadMethod: file.uploadMethod ? file.uploadMethod : 'use_inspiration',
      geminiUri: file.urlGemini,
      geminiFileType: file.type,
      geminiState: file.uploadStatus,
      geminiName: file.name,
      geminiDisplayName: file.geminiDisplayName,
    }))

    const payload = {
      title: magicData.title,
      description: magicData.description,
      type: magicData.type,
      useCase: magicData.useCase,
      language: magicData.language,
      programLength: magicData.programLength,
      files: selectedFiles,
      filesPrecision: mappingFilesWithPrecision,
      geminiData: magicData.uploadedFilesData,
      generateAiImages: magicData.generateAiImages ?? true,
      generateAiAudio: magicData.generateAiAudio ?? false,
      generateAiVideo: magicData.generateAiVideo ?? false,
      learnerOutcomes: magicData.learnerOutcomes,
    }

    const response = await createSessionOrAction({
      courseId,
      chapterId,
      organizationId,
      companyId,
      inputData: {
        title: payload.title,
        description: payload.description,
        useCase: payload.useCase,
        language: payload.language,
        type: typeOfCreatorAction ? payload.type : undefined,
        sessionLength: typeOfCreatorSession ? payload.programLength : undefined,
        learnerOutcomes: payload.learnerOutcomes,
      },
      type: typeOfCreator,
      uploadedFiles: enrichedFilesData,
      token: appState.loggedInAs.token,
      dummy: isE2eTest || false,
      generateAiImages: payload.generateAiImages,
      generateAiAudio: payload.generateAiAudio,
      generateAiVideo: payload.generateAiVideo,
      totalCreditsCount: credits.totalCredits,
      totalTokensCount: credits.totalTokens,
    })

    modalCreationInProcess.close()
    if (response.error) {
      setCreationLoader(false)
      showModal2({
        title: `Error`,
        content: extractGeminiErrorMessage(response.error.message),
        destructive: true,
        primaryText: intl.get('global_close'),
        secondaryButton: false,
      })
      return
    }

    closeModal()

    trackAiCreation({
      type: typeOfCreator,
      fileCount: selectedFiles.length,
      creditAvailable: credits.availableCredits,
      credits: credits.totalCredits,
      courseId,
      organizationId,
    })
  }
  const createMagicProgram = async () => {
    // check if the user has enough credits
    const upsellModalShown = checkAndShowAiCreditsModal(credits, subData, organizationId, sendBillingAdminLockedFeatureEmail, () => setAiCreditsRequested(true))
    if (upsellModalShown) return

    const isE2eTest = localStorage.getItem("e2eTest") === "true"
    setCreationLoader(true)

    const title = intl.get('creating_magic_program_title')
    const description = intl.get('creating_magic_program_description')

    const modalCreationInProcess = showModal2({
      component: <CreationInProgress title={title} description={description}/>,
      disableClickOutside: true,
      onlyContent: true,
      secondaryButton: false,
    })

    const mappingFilesWithPrecision = selectedFiles.map((file) => ({
      filePrecision: file.filePrecision ? file.filePrecision : 'high_precision',
      uploadMethod: file.uploadMethod ? file.uploadMethod : 'use_inspiration',
      geminiUri: file.urlGemini,
      geminiFileType: file.type,
      geminiState: file.uploadStatus,
      geminiName: file.name,
      geminiDisplayName: file.geminiDisplayName,
    }))

    const enrichedFilesData = magicData.uploadedFilesData.map(file => {
      const matchingSelectedFile = selectedFiles.find(
        selected => selected.geminiName === file.name
      )

      return {
        ...file,
        filePrecision: matchingSelectedFile?.filePrecision || 'high_precision',
        uploadMethod: matchingSelectedFile?.uploadMethod || 'use_inspiration',
      }
    })

    // Determine the actual programLength value to send
    const programLengthValue = magicData.programLength === 'other' ? String(programLengthOther) : magicData.programLength

    const payload = {
      title: magicData.title,
      description: magicData.description || '-', // magicData.courseDescription
      type: magicData.type,
      useCase: magicData.useCase,
      language: magicData.language,
      programLength: programLengthValue,
      files: selectedFiles,
      filesPrecision: mappingFilesWithPrecision,
      geminiData: magicData.uploadedFilesData,
      audience: magicData.audience === 'other' ? `Other: ${magicData.audienceOther}` : magicData.audience,
      voice: magicData.voice || 'automatic',
      generateAiImages: magicData.generateAiImages ?? true,
      generateAiAudio: magicData.generateAiAudio ?? false,
      generateAiVideo: magicData.generateAiVideo ?? false,
      programDifficulty: magicData.programDifficulty,
      learnerOutcomes: magicData.learnerOutcomes,
      programSuccess: magicData.programSuccess,
      useEmoji: magicData.useEmoji ?? true,
    }

    const response = await createProgram({
      organizationId,
      inputData: {
        title: payload.title,
        description: payload.description,
        useCase: payload.useCase,
        language: payload.language,
        audience: payload.audience,
        voice: payload.voice,
        outline: outlineDataString[outlinePosition],
        courseLength: payload.programLength,
        programDifficulty: payload.programDifficulty,
        learnerOutcomes: payload.learnerOutcomes,
        programSuccess: payload.programSuccess,
        useEmoji: payload.useEmoji ?? true,

      },
      uploadedFiles: magicData.uploadedFilesData,
      token: appState.loggedInAs.token,
      dummy: isE2eTest || false,
      generateAiImages: payload.generateAiImages,
      generateAiAudio: payload.generateAiAudio,
      generateAiVideo: payload.generateAiVideo,
      totalCreditsCount: outlineCreditsArray[outlinePosition]?.totalCredits || credits.totalCredits,
      totalTokensCount: outlineCreditsArray[outlinePosition]?.totalTokens || credits.totalTokens,
    })
    setTimeout(() => {
      modalCreationInProcess.close()
    }, 10000)
    if (response.error) {
      setCreationLoader(false)
      showModal2({
        title: `Error`,
        content: extractGeminiErrorMessage(response.error.message),
        destructive: true,
        primaryText: intl.get('global_close'),
        secondaryButton: false,
      })
      return
    }

    closeModal()
    if (showZendeskWidget) {
      showZendeskWidget()
    }
    updateAppCache('isCreatingCourse', true)

    trackAiCreation({
      type: typeOfCreator,
      fileCount: selectedFiles.length,
      creditAvailable: credits.availableCredits,
      credits: credits.totalCredits,
      organizationId,
    })
  }

  const createOutline = async (additionalInfo?: string, customMagicData?: typeof magicData) => {
    setOutlineLoading(true)

    // Use customMagicData if provided, otherwise use current magicData
    const dataToUse = customMagicData || magicData

    const mappingFilesWithPrecision = selectedFiles.map((file) => ({
      filePrecision: file.filePrecision ? file.filePrecision : 'high_precision',
      uploadMethod: file.uploadMethod ? file.uploadMethod : 'use_inspiration',
      geminiUri: file.urlGemini,
      geminiFileType: file.type,
      geminiState: file.uploadStatus,
      geminiName: file.name,
      geminiDisplayName: file.geminiDisplayName,
    }))

    // Determine the actual programLength value to send
    const programLengthValue = dataToUse.programLength === 'other' ? String(programLengthOther) : dataToUse.programLength

    const payload = {
      title: dataToUse.title,
      description: dataToUse.description,
      describeChanges: describeChanges,
      additionalInfo: additionalInfo,
      type: dataToUse.type,
      useCase: dataToUse.useCase,
      language: dataToUse.language,
      programLength: programLengthValue,
      files: selectedFiles,
      filesPrecision: mappingFilesWithPrecision,
      geminiData: dataToUse.uploadedFilesData,
      audience: dataToUse.audience === 'other' ? `Other: ${dataToUse.audienceOther}` : dataToUse.audience,
      voice: dataToUse.voice,
      previousOutline: outlineDataString[outlinePosition] || null,
      programDifficulty: dataToUse.programDifficulty,
      learnerOutcomes: dataToUse.learnerOutcomes,
      programSuccess: dataToUse.programSuccess,
      useEmoji: dataToUse.useEmoji ?? true,
    }

    let response
    try {
      response = await Promise.race([
        createProgramOutline({
          organizationId,
          inputData: {
            title: payload.title,
            description: payload.description,
            describeChanges: payload.describeChanges,
            additionalInfo: payload.additionalInfo,
            useCase: payload.useCase,
            language: payload.language,
            courseLength: payload.programLength,
            audience: dataToUse.audience === 'other' ? `Other: ${dataToUse.audienceOther}` : dataToUse.audience,
            voice: payload.voice,
            previousOutline: payload.previousOutline,
            programDifficulty: payload.programDifficulty,
            learnerOutcomes: payload.learnerOutcomes,
            programSuccess: payload.programSuccess,
            useEmoji: payload.useEmoji ?? true,
          },
          uploadedFiles: magicData.uploadedFilesData,
          token: appState.loggedInAs.token,
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), 180000)) // 60-second timeout

      ])
    } catch (error) {
      const errorMessage = (error instanceof Error && error.message === 'Request timed out')
        ? intl.get('error_request_timed_out')
        : intl.get('error_service_busy');

      response = {data: "", error: {message: errorMessage}}

      showModal2({
        title: `Error`,
        content: errorMessage,
        destructive: true,
        primaryText: intl.get('global_close'),
        secondaryButton: false,
      })
    }

    setOutlineData(response.data)

    setOutlineLoading(false)
    track({
      event: typeOfCreator,
      variables: {
        organizationId,

      }
    })
  }


  const responsive = {
    10000000: {
      items: 1,
    },
    0: {
      items: 1,
    },
  };

  const fetchCustomizeSuggestions = async (showLoading = true) => {
    if (showLoading) {
      setCustomizeSuggestionsLoading(true)
    }

    const enrichedFilesData = magicData.uploadedFilesData.map(file => {
      const matchingSelectedFile = selectedFiles.find(
        selected => selected.geminiName === file.name
      )

      return {
        ...file,
        filePrecision: matchingSelectedFile?.filePrecision || 'high_precision',
        uploadMethod: matchingSelectedFile?.uploadMethod || 'use_inspiration',
      }
    })

    try {
      const response = await generateCustomizeSuggestions({
        organizationId,
        inputData: {
          title: magicData.title,
          description: magicData.description,
          learnerOutcomes: magicData.learnerOutcomes,
          programSuccess: magicData.programSuccess,
        },
        uploadedFiles: enrichedFilesData,
        token: appState.loggedInAs.token,
      })

      if (response.data) {
        const suggestions = response.data

        // Map language code to language key
        const languageKey = suggestions.language?.languageCode || 'automatic'

        // Handle courseLength
        let programLengthValue: 'automatic' | 'other' | number = 'automatic'
        if (suggestions.courseLength) {
          if ([3, 6, 10].includes(suggestions.courseLength)) {
            programLengthValue = suggestions.courseLength
          } else {
            programLengthValue = 'other'
          }
        }

        // Build the update object - only update fields that are currently set to 'automatic'
        const updates: any = { ...magicData }

        if (magicData.useCase === 'automatic' && suggestions.useCase) {
          updates.useCase = suggestions.useCase
        }
        if (magicData.language === 'automatic' && languageKey) {
          updates.language = languageKey
        }
        if (magicData.programLength === 'automatic') {
          updates.programLength = programLengthValue
          // Only update programLengthOther if programLength is automatic
          if (programLengthValue === 'other' && suggestions.courseLength) {
            setProgramLengthOther(suggestions.courseLength)
          }
        }
        if (magicData.audience === 'automatic' && suggestions.audience) {
          updates.audience = suggestions.audience
          // If audience is "other", also populate audienceOther field
          if (suggestions.audience === 'other' && suggestions.audienceOther) {
            updates.audienceOther = suggestions.audienceOther
          }
        }
        if (magicData.voice === 'automatic' && suggestions.voice) {
          updates.voice = suggestions.voice
        }
        if (magicData.programDifficulty === 'automatic' && suggestions.programDifficulty) {
          updates.programDifficulty = suggestions.programDifficulty
        }

        setMagicData(updates)
        setSuggestionsFetched(true)

        // Return the updated data so it can be used immediately
        return updates
      }
    } catch (error) {
      console.error('Error fetching customize suggestions:', error)
    } finally {
      if (showLoading) {
        setCustomizeSuggestionsLoading(false)
      }
    }

    // Return null if fetch failed or no data
    return null
  }

  const showAlertOnClose = () => {
    if (magicData.title || magicData.learnerOutcomes || magicData.programSuccess || selectedFiles.length > 0 || outlineDataString.length > 0) {
      const modal = showModal2({
        component: <ExitMagicCreatorAlert
          onContinue={() => {
            onPrevious && onPrevious()
            if (showZendeskWidget) {
              showZendeskWidget()
            }
            setCurrentStep('')
            modal.close()
          }}
          closeModal={() => modal.close()}
        />,
        onlyContent: true,
      })
    } else {
      if (onPrevious) {
        onPrevious()
      }
      if (showZendeskWidget) {
        showZendeskWidget()
      }
      setCurrentStep('')
  }}

  const showAlertOnOutlineBack = () => {
    const modal = showModal2({
      component: <OutlineAlert
        onContinue={() => {
          setProgramStep(2)
          setOutlineData('')
          setOutlineDataArray([])
          setOutlineDataString([])
          setOutlineCreditsArray([])
          setDescribeChanges('')
          setOutlinePosition(0)
          modal.close()
        }}
        closeModal={() => modal.close()}
      />,
      onlyContent: true,
    })
  }

  return ( <div className={`${!expanded && 'sm:p-8'} relative h-[99.9%] overflow-auto`} style={whiteLabelData ? { backgroundColor: whiteLabelData.bgColor || '#f9f7f2' } : undefined}>
    {typeOfCreatorProgram && onPrevious && programStep === 1 ? <button className="flex gap-2 text-actions-multiple_choice font-bold mb-8 pt-8 pl-8 sm:pt-0 sm:pl-0" onClick={showAlertOnClose}>
      <ArrowLeftIcon className="w-6 h-6" /> {intl.get('global_back')}
    </button> : typeOfCreatorProgram && programStep === 2 ? <button className="flex gap-2 text-actions-multiple_choice font-bold mb-8 pt-8 pl-8 sm:pt-0 sm:pl-0" onClick={() => setProgramStep(1)}>
      <ArrowLeftIcon className="w-6 h-6" /> {intl.get('global_back')}
    </button> : typeOfCreatorProgram && programStep === 3 ? <button className="flex gap-2 text-actions-multiple_choice font-bold mb-8 pt-8 pl-8 sm:pt-0 sm:pl-0" onClick={showAlertOnOutlineBack}>
      <ArrowLeftIcon className="w-6 h-6" /> {intl.get('global_back')}
    </button>
    : typeOfCreatorProgram && <button className="flex gap-2 text-actions-multiple_choice font-bold mb-8 pt-8 pl-8 sm:pt-0 sm:pl-0" onClick={() => {
      setProgramStep(3)
    }}>
    <ArrowLeftIcon className="w-6 h-6" /> {intl.get('global_back')}
  </button>
    }
    <div className="flex items-center justify-start w-full space-x-4 mb-2 px-8 sm:px-0">
      <img className="w-5 h-5 sm:w-7 sm:h-7" src="/images/wand-magic-sparkles.svg" alt="magic wand" />
      <div>
        <div className={`${typeOfCreatorProgram ? 'text-3xl': 'text-lg sm:text-2xl'}`}>{intl.get('gnowbe_ai_magic_creator')}</div>
      </div>
    </div>
    <div className={`${typeOfCreatorProgram ? 'text-sm px-8 sm:px-0 mb-6': 'text-xs sm:text-sm px-8 sm:px-0'}`}>
      {typeOfCreatorAction ? intl.getHTML('magic_action_creator_description') : typeOfCreatorSession ? intl.getHTML('magic_session_creator_description') : intl.get('magic_program_creator_description')}
    </div>
    {typeOfCreatorProgram && <ProgressIndicator currentStep={programStep} />}
    {!typeOfCreatorProgram &&
    <div className="my-5 px-4 sm:px-0">
      <button data-test="provide-more-details" className="flex text-sm sm:text-base items-center justify-center" onClick={() => {
        trackButtonEnhanced({
          button: `Gnowbe Ai ${capitalize(typeOfCreator)} Provide More Details`,
          onScreen: `Magic ${capitalize(typeOfCreator)} Creator`,
        })
        setShowDetails(!showDetails)
        }}>
          {intl.get('magic_provide_more_details')}
        <ChevronDownIcon className={`w-5 h-5 ml-2 transform transition-transform duration-300 ease-in-out ${showDetails ? 'rotate-180' : ''}`} />
      </button>
    </div>}
    {showDetails && programStep === 1 && (
      <div className={`mt-6 mb-8 ${typeOfCreatorProgram && 'p-5 rounded-lg bg-white'}`}>
        {typeOfCreatorProgram && <StepTitle title={intl.get('program_details_text')} description={intl.get('program_details_description')} />}
        <form className="space-y-6">
          <div className={wrapperInputCn}>
            <div className="flex flex-row space-x-2">
              <label htmlFor="magicActionTitle" className={labelCn}>{typeOfCreatorAction ? intl.get('global_action_title') : typeOfCreatorSession ? intl.get('chapter_placeholder_title') : intl.get('title')}</label>
              {typeOfCreatorProgram && <AdditionalInfo
                tooltipContent={<div>{intl.get('gnowbe_ai_progral_title_tooltip')}</div>}
                interactive={true} />}
            </div>
            <input
              name="title"
              id="magicActionTitle"
              placeholder={typeOfCreatorAction ? intl.get('global_action_title') : typeOfCreatorSession ? intl.get('chapter_placeholder_title') : intl.get('program_title')}
              className={`${inputAreaCn} shadow-[0_0px_10px_0px_rgba(0,0,0,0.2)]`}
              type="text"
              maxLength={200}
              value={magicData.title || ''}
              onChange={(e) => handleInputChange(e)}/>
              <div className='text-xs text-gray-500 text-right'>{magicData.title.length || 0}/200</div>
          </div>
          {typeOfCreatorProgram && <MagicUploader onChange={(e) => handleInputChange(e)} magicData={magicData} setMagicData={setMagicData} showUploadIndicator={showUploadIndicator} setShowUploadIndicator={setShowUploadIndicator} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} typeOfCreator="course" courseId={courseId} organizationId={organizationId} />}
          {typeOfCreatorAction && <MagicSelect
            name="type"
            labelText={intl.get('magic_choose_action_type')}
            content={filteredContentTypes}
            onChange={(e) => handleInputChange(e)}
            selectedValue={magicData.type}
          />}
          {!typeOfCreatorProgram && <MagicTextArea
              typeOfCreator={typeOfCreator}
              magicData={magicData.learnerOutcomes}
              handleInputChange={handleInputChange}
              label={typeOfCreatorSession ? intl.get('gnowbe_ai_learner_outcome_label_text_session') : intl.get('gnowbe_ai_learner_outcome_label_text_action')}
              id="magicLearnerOutcomes"
              name="learnerOutcomes"
              placeholder={typeOfCreatorSession ? intl.get('gnowbe_ai_learner_outcome_placeholder_session'): intl.get('gnowbe_ai_learner_outcome_placeholder_action')}
              additionalInfoText={typeOfCreatorSession ? intl.get('gnowbe_ai_learner_outcome_additional_info_session') : intl.get('gnowbe_ai_learner_outcome_additional_info_action')}
            />}
          {typeOfCreatorProgram && <>
            <MagicTextArea
              typeOfCreator={typeOfCreator}
              magicData={magicData.learnerOutcomes}
              handleInputChange={handleInputChange}
              label={intl.get('gnowbe_ai_learner_outcome_label_text')}
              id="magicLearnerOutcomes"
              name="learnerOutcomes"
              placeholder={intl.get('gnowbe_ai_learner_outcome_placeholder')}
              additionalInfoText={intl.get('gnowbe_ai_learner_outcome_additional_info')}
            />
            <MagicTextArea
              typeOfCreator={typeOfCreator}
              magicData={magicData.programSuccess}
              handleInputChange={handleInputChange}
              label={intl.get('gnowbe_ai_program_success_label_text')}
              id="magicProgramSuccess"
              name="programSuccess"
              placeholder={intl.get('gnowbe_ai_program_success_placeholder')}
              additionalInfoText={intl.get('gnowbe_ai_program_success_additional_info')}
            />
          </>}

          <div>
            {typeOfCreatorSession && <MagicUploader onChange={(e) => handleInputChange(e)} magicData={magicData} setMagicData={setMagicData} showUploadIndicator={showUploadIndicator} setShowUploadIndicator={setShowUploadIndicator} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} typeOfCreator={typeOfCreator} courseId={courseId} organizationId={organizationId} />}
          </div>
          {!typeOfCreatorProgram && <button data-test="more-options" className="flex text-sm sm:text-base items-center justify-center mt-2" onClick={(e) => {
            e.preventDefault()
            trackButtonEnhanced({
              button: `Gnowbe Ai ${capitalize(typeOfCreator)} More Options`,
              onScreen: `Magic ${capitalize(typeOfCreator)} Creator`,
            })
            setShowOptions(!showOptions)
          }}>
            {intl.get('magic_more_options')}
            <ChevronDownIcon className={`w-5 h-5 ml-2 transform transition-transform duration-300 ease-in-out ${showOptions ? 'rotate-180' : ''}`} />
          </button>}
          {showOptions && !typeOfCreatorProgram && (
            <div className="flex flex-col space-y-2 w-full bg-[#F9F8F8] rounded-lg relative">
              <div className="mb-4">
                {typeOfCreatorAction && <MagicUploader onChange={(e) => handleInputChange(e)} magicData={magicData} setMagicData={setMagicData} showUploadIndicator={showUploadIndicator} setShowUploadIndicator={setShowUploadIndicator} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} typeOfCreator={typeOfCreator} courseId={courseId} organizationId={organizationId} />}
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <MagicSelect
                  name="useCase"
                  labelText={intl.get('magic_use_case')}
                  content={Object.keys(useCases).map(key => [key, useCases[key]])}
                  onChange={(e) => handleInputChange(e)}
                  selectedValue={magicData.useCase}
                  hasInfo={true}
                  tooltipContent={intl.get('magic_use_case_tooltip')}
                  isHalfWidth={typeOfCreatorAction}
                />
                <MagicSelect
                  name="language"
                  labelText={intl.get('magic_language')}
                  content={Object.entries(languages)}
                  onChange={(e) => handleInputChange(e)}
                  selectedValue={magicData.language}
                  hasInfo={true}
                  tooltipContent={intl.get('magic_language_tooltip')}
                  isHalfWidth={typeOfCreatorAction}
                />
                {typeOfCreatorSession && (
                  <MagicSelect
                    name="programLength"
                    labelText={typeOfCreatorProgram ? intl.get('magic_program_length') : intl.get('magic_session_length')}
                    content={Object.keys(typeOfCreatorProgram ? programLength : sessionLength).map(key => [key, (typeOfCreatorProgram ? programLength : sessionLength)[key]])}
                    onChange={(e) => handleInputChange(e)}
                    selectedValue={magicData.programLength}
                    hasInfo={true}
                    tooltipContent={`${
                      typeOfCreatorProgram
                        ? intl.get('magic_program_length_info')
                        : intl.get('magic_session_length_info')
                    }`}
                  />
                  )
                }
              </div>
              <div className={`mt-4 grid grid-cols-2 gap-4 px-4 ${isAudienceOther ? 'pt-12' : 'pt-2'} pb-4`}>
                <MagicCheckbox name={intl.get('gnowbe_ai_generate_images')} setMagicData={setMagicData} fieldKey="generateAiImages" additionalInfoText={intl.get('gnowbe_ai_generate_images_tooltip')} />
                <MagicCheckbox name={intl.get('gnowbe_ai_use_emojis')} setMagicData={setMagicData} fieldKey="useEmoji" additionalInfoText={intl.get('gnowbe_ai_use_emojis_tooltip')} />
                
                {!typeOfCreatorAction && <MagicCheckbox name={intl.get('gnowbe_ai_generate_audio')} setMagicData={setMagicData} fieldKey="generateAiAudio" additionalInfoText={intl.get('gnowbe_ai_generate_audio_tooltip')} />}
                {/* No AI video toggle: the new app makes AI videos; the API ignores the flag here (GNB-3141). */}
              </div>
            </div>
          )}
        </form>
      </div>
    )}

    {programStep === 2 && typeOfCreatorProgram && (
      <div className={`mt-6 mb-8 p-5 rounded-lg bg-white`}>
        <div className="flex items-start justify-between mb-4">
          <StepTitle title={intl.get('magic_more_options')} description={intl.get('magic_more_options_description')} />
          <Tippy content={intl.get('magic_ai_suggestions_tooltip')} placement="bottom" disabled={hasAutomaticFields()}>
            <div>
              <button
                onClick={() => fetchCustomizeSuggestions()}
                disabled={customizeSuggestionsLoading || !hasAutomaticFields()}
                className="flex items-center gap-2 px-4 py-2 bg-magic text-white rounded-lg hover:bg-magic-dark disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                data-test="fetch-suggestions-btn"
              >
                <MagicWandSparklesIcon className="fill-white h-4 w-4" />
                {customizeSuggestionsLoading ? intl.get('loading') : intl.get('magic_get_ai_suggestions')}
              </button>
            </div>
          </Tippy>
        </div>
        <form className="space-y-6">
          <div className="flex flex-col space-y-2 w-full bg-[#F9F8F8] rounded-lg relative p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between">
              <MagicSelect
                name="useCase"
                labelText={intl.get('magic_use_case')}
                content={Object.keys(useCases).map(key => [key, useCases[key]])}
                onChange={(e) => handleInputChange(e)}
                selectedValue={magicData.useCase}
                hasInfo={true}
                tooltipContent={intl.get('magic_use_case_tooltip')}
                isHalfWidth={true}
                isLoading={customizeSuggestionsLoading && magicData.useCase === 'automatic'}
              />
              <MagicSelect
                name="language"
                labelText={intl.get('magic_language')}
                content={Object.entries(languages)}
                onChange={(e) => handleInputChange(e)}
                selectedValue={magicData.language}
                hasInfo={true}
                tooltipContent={intl.get('magic_language_tooltip')}
                isHalfWidth={true}
                isLoading={customizeSuggestionsLoading && magicData.language === 'automatic'}
              />
            </div>
            <div className={`flex flex-col sm:flex-row ${magicData.programLength === 'other' ? 'items-start justify-start' : 'items-center justify-between'}`}>
              <MagicSelect
                name="programLength"
                labelText={intl.get('magic_program_length')}
                content={Object.keys(programLength).map(key => [key, programLength[key]])}
                onChange={(e) => handleInputChange(e)}
                selectedValue={magicData.programLength}
                hasInfo={true}
                tooltipContent={intl.get('magic_program_length_info')}
                isHalfWidth={true}
                showOtherInput={true}
                otherValue={programLengthOther}
                onOtherChange={handleProgramLengthOtherChange}
                isLoading={customizeSuggestionsLoading && magicData.programLength === 'automatic'}
              />
              <MagicSelect
                name="voice"
                labelText={intl.get('gnowbe_ai_tone_of_voice')}
                content={Object.keys(voice).map(key => [key, voice[key]])}
                isHalfWidth={true}
                onChange={(e) => handleInputChange(e)}
                selectedValue={magicData.voice}
                hasInfo={true}
                tooltipContent={intl.get('gnowbe_ai_tone_of_voice_tooltip')}
                isLoading={customizeSuggestionsLoading && magicData.voice === 'automatic'}
              />
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between">
              <div className={`${wrapperInputCn} sm:w-1/2 order-3 sm:order-none relative`}>
                <div className="flex flex-row space-x-2">
                  <label htmlFor="audience" className={labelCn}>{intl.get('gnowbe_ai_target_audience')}</label>
                  <AdditionalInfo tooltipContent={intl.get('gnowbe_ai_target_audience_tooltip')} interactive />
                </div>
                {customizeSuggestionsLoading && magicData.audience === 'automatic' ? (
                  <div className="h-11 bg-gray-200 skeleton-box" />
                ) : (
                  <>
                    <select
                      id="audience"
                      name="audience"
                      className={`${inputAreaCn} shadow-[0_0px_10px_0px_rgba(0,0,0,0.2)]`}
                      value={magicData.audience || ''}
                      onChange={(e) => handleInputChange(e)}>
                      <option value="automatic">{intl.get('automatic')}</option>
                      {Object.keys(audienceOptions).map((key, index) => {
                        return <option value={key} key={`${key}-${index}`}>{audienceOptions[key]}</option>
                      })}
                    </select>
                    {isAudienceOther && <input
                        name="audienceOther"
                        id="magicAudience"
                        placeholder={intl.get('gnowbe_ai_target_audience_placeholder')}
                        className={`${inputAreaCn} shadow-[0_0px_10px_0px_rgba(0,0,0,0.2)] w-1/2 sm:w-[284px] absolute -bottom-10 left-4`}
                        type="text"
                        maxLength={200}
                        value={magicData.audienceOther || ''}
                        onChange={(e) => handleInputChange(e)}
                        />}
                  </>
                )}
                </div>

              <MagicSelect
                name="programDifficulty"
                labelText={intl.get('gnowbe_ai_program_difficulty')}
                content={Object.keys(programDifficulty).map(key => [key, programDifficulty[key]])}
                onChange={(e) => handleInputChange(e)}
                selectedValue={magicData.programDifficulty}
                hasInfo={true}
                tooltipContent={intl.get('gnowbe_ai_program_difficulty_tooltip')}
                isHalfWidth={true}
                isLoading={customizeSuggestionsLoading && magicData.programDifficulty === 'automatic'}
              />
            </div>
            <div className={`mt-4 grid grid-cols-2 gap-4 px-4 ${isAudienceOther ? 'pt-12' : 'pt-2'} pb-4`}>
              <MagicCheckbox name={intl.get('gnowbe_ai_generate_images')} setMagicData={setMagicData} fieldKey="generateAiImages" additionalInfoText={intl.get('gnowbe_ai_generate_images_tooltip')} />
              <MagicCheckbox name={intl.get('gnowbe_ai_use_emojis')} setMagicData={setMagicData} fieldKey="useEmoji" additionalInfoText={intl.get('gnowbe_ai_use_emojis_tooltip')} />

              <MagicCheckbox name={intl.get('gnowbe_ai_generate_audio')} setMagicData={setMagicData} fieldKey="generateAiAudio" additionalInfoText={intl.get('gnowbe_ai_generate_audio_tooltip')} />
              {/* No AI video toggle: the new app makes AI videos; the API ignores the flag here (GNB-3141). */}
            </div>
          </div>
        </form>
      </div>
    )}

    {programStep === 3 && <div className={`mt-6 mb-8 ${typeOfCreatorProgram && !expanded && 'p-5 rounded-lg bg-white'}`}>
      <StepTitle title={intl.get('gnowbe_ai_program_outline')} description={intl.get('gnowbe_ai_program_outline_description')} />
      <form className={`${expanded ? 'static' : 'relative'}`}>
        <div className="flex flex-col">
          <div className={`bg-[#F9F8F8] rounded-lg pl-8 pr-4 py-10 overflow-hidden ${expanded && 'absolute top-0 h-full sm:h-screen w-full rounded-none z-50'}`} data-test="program-outline">
            {!outlineLoading && <div className="relative">
              {!expanded
                ? <img className="w-5 h-5 absolute -top-7 right-0 cursor-pointer" src="/images/opens-in-full-new.svg" alt="open full image" onClick={() => setExpanded(true)} />
                : <img className="w-5 h-5 absolute -top-7 right-0 cursor-pointer" src="/images/minus.svg" alt="close full image" onClick={() => setExpanded(false)} />
                }
            </div>}
            {(outlineLoading || customizeSuggestionsLoading) && <div className="h-96 -ml-5 -mt-5 mb-5">
              <Lottie animationData={outlineSkeleton} />
            </div>}
            {outlineData && <div className={`no-max-width ${outlineDataArray.length === 1 && '-mb-5'}`} key={outlineDataArray.length}>
              <AliceCarousel
                disableDotsControls
                responsive={responsive}
                activeIndex={outlinePosition + 1}
                onSlideChanged={(e) => {
                  outlineDataArray.filter((t, i) => {
                    if (i === e.item) {
                      setOutlinePosition(i)
                    }
                  })
                }}

                renderPrevButton={({ isDisabled }) => (
                  <button type="button" className={`absolute bottom-4 right-[86px] sm:right-14 bg-magic-light disabled:bg-magic-lighter disabled:cursor-not-allowed rounded-full shadow-md p-2 ${outlineDataArray.length <= 1 && 'invisible'}`} disabled={isDisabled} data-test="outline-prev-btn">
                    <ArrowLeftIcon className="h-4 w-4" />
                  </button>
                )}
                renderNextButton={({ isDisabled }) => (
                  <button type="button" className={`absolute bottom-4 right-12 sm:right-5 bg-magic-light disabled:bg-magic-lighter disabled:cursor-not-allowed rounded-full shadow-md p-2 ${outlineDataArray.length <= 1 && 'invisible'}`} disabled={isDisabled} data-test="outline-next-btn">
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                )}
                items={outlineDataArray.map((outline, i) => (
                  <OutlineContent key={i} parsedData={outline} expanded={expanded} />
                ))}
              />
              {outlineDataArray.length > 1 && <div className="absolute bottom-5 left-8 text-sm">
                <strong>Outline:</strong> {`${outlinePosition + 1}/${outlineDataArray.length}`}
              </div>}
            </div>}
          </div>
        </div>
      </form>
      {!expanded && <button data-test="more-options" className="flex text-sm sm:text-base items-center justify-center pt-4 pb-2 sm:pb-0" onClick={(e) => {
        e.preventDefault()
        trackButtonEnhanced({
          button: `Gnowbe Ai ${capitalize(typeOfCreator)} Edit Outline`,
          onScreen: `Magic ${capitalize(typeOfCreator)} Creator`,
        })
        setShowEditOutline(!showEditOutline)
      }}>
        {intl.get('gnowbe_ai_edit_outline')}
        <ChevronDownIcon className={`w-5 h-5 ml-2 transform transition-transform duration-300 ease-in-out ${showEditOutline ? 'rotate-180' : ''}`} />
      </button>}
      {showEditOutline && <>
        <div className="flex flex-col sm:flex-row w-full items-center gap-y-3 sm:gap-x-3 justify-between mt-2">
          <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-y-3 sm:gap-y-0 sm:gap-x-3">
            <Button text={intl.get('gnowbe_ai_regenerate')} className={recreateOutlineBtns} onClick={() => {
              setOutlineData('')
              createOutline('Regenerate')
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Regenerate',
                onScreen: 'Magic Program Creator',
              })
              }} />
            <Button text={intl.get('gnowbe_ai_make_fun')} className={recreateOutlineBtns} onClick={() => {
              setOutlineData('')
              createOutline('Make it more fun')
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Make It More Fun',
                onScreen: 'Magic Program Creator',
              })
              }} />
          </div>
          <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-y-3 sm:gap-y-0 sm:gap-x-3">
            <Button text={intl.get('gnowbe_ai_make_professional')} className={recreateOutlineBtns} onClick={() => {
              setOutlineData('')
              createOutline('Make it more professional')
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Make It More Professional',
                onScreen: 'Magic Program Creator',
              })
              }} />
            <Button text={intl.get('add_another_session')} className={recreateOutlineBtns} onClick={() => {
              setOutlineData('')
              createOutline('Add One More Session')
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Add Another Session',
                onScreen: 'Magic Program Creator',
              })
              }} />
          </div>
        </div>
        <div className={`${wrapperInputCn} mt-6`}>
          <label htmlFor="magicDescribeChanges" className={labelCn}>{intl.get('gnowbe_ai_edit_outline_text')}</label>
          <textarea
            name="describeChanges"
            id="magicDescribeChanges"
            placeholder={intl.get('gnowbe_ai_describe_main_goals_placehoder')}
            className={`${inputAreaCn} shadow-[0_0px_10px_0px_rgba(0,0,0,0.2)] min-h-[144px]`}
            value={describeChanges || ''}
            onChange={(e) => setDescribeChanges(e.target.value)}
            maxLength={1000}
          />
          <div className="flex items-center justify-between mt-1">
            <div className='text-xs text-gray-500'>{describeChanges.length}/1000</div>
            {!expanded && <Button text={intl.get('gnowbe_ai_update_outline')} className={`${recreateOutlineBtns} ${describeChanges ? 'cursor-pointer' : 'cursor-not-allowed'}`} disabled={!describeChanges} onClick={() => {
              if (!describeChanges) return
              setOutlineData('')
              createOutline(describeChanges)
              setDescribeChanges('')
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Send Outline Changes',
                onScreen: 'Magic Program Creator',
              })
            }} />}
          </div>
        </div>
      </>}
    </div>}
    {programStep === 4 && <div>
        <div className={`mt-6 mb-8 ${typeOfCreatorProgram && !expanded && 'p-5 rounded-lg bg-white'}`}>
        <StepTitle title={intl.get('gnowbe_ai_summary_title')} description={intl.get('gnowbe_ai_summary_description')} />
        <ProgramSummaryField outLineData={outlineDataArray[outlinePosition]} />
        {magicData.uploadedFilesData.length > 0 && <ProgramSummaryField uploadData={magicData.uploadedFilesData} />}
        <ProgramSummaryField style={magicData} />
        <ProgramSummaryField settings={magicData} />
      </div>
        <ProgramFinalCosts
          credits={outlineCreditsArray[outlinePosition] || credits}
          organizationId={organizationId}
          canAssignAiTasks={canAssignAiTasks}
          alreadyRequested={aiCreditsRequested}
          onAiCreditsRequested={() => setAiCreditsRequested(true)} />
    </div>}

    <div className={`flex flex-col sm:flex-row ${programStep !== 4 && !(typeOfCreatorProgram && programStep === 1)  ? 'items-start justify-start sm:items-center sm:justify-between' : 'items-start justify-start sm:items-end sm:justify-end'} mt-10 sm:space-x-4 pr-4 sm:pr-0 pl-8 pb-8 sm:pl-0 sm:pb-0`}>
      {programStep !== 4 && !(typeOfCreatorProgram && programStep === 1) && <MagicCredits
        magicData={magicData}
        creditsTokens={credits}
        setCredits={setCredits}
        showCreditsIndicator={showCreditsIndicator}
        setShowCreditsIndicator={setShowCreditsIndicator}
        courseId={courseId}
        organizationId={organizationId}
        type={typeOfCreator}
        programLength={magicData.programLength}
        programLengthOther={programLengthOther}
        outlineData={outlineDataString[outlinePosition]}
        outlineDataStringArray={outlineDataString}
        outlineCreditsArray={outlineCreditsArray}
        outlinePosition={outlinePosition}
        skipEstimateTokens={typeOfCreatorProgram && programStep === 2 && customizeSuggestionsLoading}
        canAssignAiTasks={canAssignAiTasks}
        alreadyRequested={aiCreditsRequested}
        onAiCreditsRequested={() => setAiCreditsRequested(true)}
      />}
      {typeOfCreatorProgram && programStep === 1 ? <div className="flex w-full mt-5 items-center justify-end sm:w-auto sm:mt-0 gap-3">
        <Button
          text={intl.get('next_text')}
          type="magic"
          className="px-3 sm:px-6 w-full sm:w-auto"
          disabled={!magicData.title}
          onClick={() => {
            setProgramStep(2)
            trackButtonEnhanced({
              button: 'Gnowbe Ai Course Next to step 2',
              onScreen: 'Magic Program Creator',
            })
          }}
          svgRightIcon={<ArrowRightIcon className="h-6 w-6 font-bold fill-white ml-2" />}
        />
      </div>  : typeOfCreatorProgram && programStep === 2 ? <div className="flex w-full mt-5 items-center justify-between sm:w-auto sm:mt-0 gap-3">
        <Button
            text={intl.get('global_back')}
            type="magic"
            className={`${recreateOutlineBtns} px-3 sm:px-6 w-full sm:w-auto`}
            onClick={() => {
              setProgramStep(1)
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Back to step 1',
                onScreen: 'Magic Program Creator',
              })
            }}
            svgIcon={<ArrowLeftIcon className="h-6 w-6 font-bold fill-[#101010] mr-2" />}
          />
        <Button
            text={intl.get('next_text')}
            type="magic"
            disabled={customizeSuggestionsLoading || disableCreateButton || (magicData.audience === 'other' && magicData.audienceOther === '')}
            className="px-3 sm:px-6 w-full sm:w-auto"
            onClick={async () => {
              // Move to step 3 immediately
              setOutlineData('')
              setProgramStep(3)
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Next to step 3',
                onScreen: 'Magic Program Creator',
              })

              // Auto-fetch suggestions if there are any automatic fields
              let updatedMagicData = undefined
              if (hasAutomaticFields()) {
                updatedMagicData = await fetchCustomizeSuggestions().catch(() => {
                  // Silently fail - leave fields as automatic if fetch fails
                  return undefined
                })
              }

              // Now create outline with the updated data (either from suggestions or existing values)
              createOutline(undefined, updatedMagicData)
            }}
            svgRightIcon={<ArrowRightIcon className="h-6 w-6 font-bold fill-white ml-2" />}
          />
      </div>
      : typeOfCreatorProgram && programStep === 3 ? <div className="flex w-full mt-5 items-center justify-between sm:w-auto sm:mt-0 gap-3">
        <Button
            text={intl.get('global_back')}
            type="magic"
            className={`${recreateOutlineBtns} px-3 sm:px-6 w-full sm:w-auto`}
            onClick={() => {
              showAlertOnOutlineBack()
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Back to step 2',
                onScreen: 'Magic Program Creator',
              })
            }}
            svgIcon={<ArrowLeftIcon className="h-6 w-6 font-bold fill-[#101010] mr-2" />}
          />
        <Button
            text={intl.get('next_text')}
            type="magic"
            disabled={!outlineData || outlineLoading || (magicData.audience === 'other' && magicData.audienceOther === '')}
            className="px-3 sm:px-6 w-full sm:w-auto"
            onClick={() => {
              setProgramStep(4)
              trackButtonEnhanced({
                button: 'Gnowbe Ai Course Next to step 4',
                onScreen: 'Magic Program Creator',
              })
            }}
            svgRightIcon={<ArrowRightIcon className="h-6 w-6 font-bold fill-white ml-2" />}
          />
      </div>
      : typeOfCreatorProgram && <div className="flex w-full mt-5 items-center justify-between sm:w-auto sm:mt-0 gap-3">
        <Button
          text={intl.get('global_back')}
          type="magic"
          className={`${recreateOutlineBtns} px-3 sm:px-6`}
          onClick={() => {
            setProgramStep(3)
            trackButtonEnhanced({
              button: 'Gnowbe Ai Course Back to step 3',
              onScreen: 'Magic Program Creator',
            })
          }}
          svgIcon={<ArrowLeftIcon className="h-6 w-6 font-bold fill-[#101010] mr-2" />}
        />
        <Button
          text={intl.get('magic_create_now')}
          type="magic"
          className="px-3 sm:px-6 w-full sm:w-auto"
          disabled={disableCreateButton || outlineLoading}
          onClick={() => {
            createMagicProgram()
            trackButtonEnhanced({
              button: 'Gnowbe Ai Course Create Now',
              onScreen: 'Magic Program Creator',
            })
          }}
          svgIcon={<MagicWandSparklesIcon className="fill-white mr-4 h-" />}
          />
      </div>}
      {typeOfCreator !== 'course' && <Button
        text={intl.get('magic_create_now')}
        type="magic"
        className="px-3 sm:px-6"
        disabled={disableCreateButton}
        onClick={() => {
          handleMagicActionCreate()
          trackButtonEnhanced({
            button: `Gnowbe Ai ${typeOfCreator} Create Now`,
            onScreen: `'Magic ${typeOfCreator} Creator'`,
          })
        }}
        svgIcon={<MagicWandSparklesIcon className="fill-white mr-4" />}
        />}
    </div>
  </div>
  )
}

export default MagicCreator